import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Effect, Match } from "effect";

import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectMutation, runEffectQuery } from "#effect-query.ts";
import { useLanguage } from "#i18n.tsx";

const passkeyQueryKey = ["settings", "passkeys"] as const;

/** Register and revoke passkeys belonging to the current staff identity. */
export function PasskeySettings() {
  const { api } = useAuth();
  const { language, t } = useLanguage();
  const queryClient = useQueryClient();
  const [name, setName] = useState(() => t("passkeys.defaultName"));
  const [notice, setNotice] = useState<string>();
  const passkeys = useQuery({
    queryKey: passkeyQueryKey,
    retry: false,
    queryFn: ({ signal }) =>
      runEffectQuery(
        Effect.promise(() => api.listPasskeys()).pipe(
          Effect.flatMap((result) =>
            result.ok ? Effect.succeed(result.value) : Effect.fail(result.error),
          ),
          Effect.withSpan("PasskeySettings.list"),
        ),
        signal,
      ),
  });
  const addPasskey = useMutation({
    retry: false,
    mutationFn: (label: string) =>
      runEffectMutation(
        Effect.promise(() => api.addPasskey(label)).pipe(
          Effect.flatMap((result) =>
            result.ok ? Effect.succeed(result.value) : Effect.fail(result.error),
          ),
          Effect.withSpan("PasskeySettings.add"),
        ),
      ),
    onSuccess: () => {
      setNotice(t("passkeys.added"));
      void queryClient.invalidateQueries({ queryKey: passkeyQueryKey });
    },
  });
  const removePasskey = useMutation({
    retry: false,
    mutationFn: (id: string) =>
      runEffectMutation(
        Effect.promise(() => api.deletePasskey(id)).pipe(
          Effect.flatMap((result) =>
            result.ok ? Effect.void : Effect.fail(result.error),
          ),
          Effect.withSpan("PasskeySettings.remove"),
        ),
      ),
    onSuccess: () => {
      setNotice(t("passkeys.removed"));
      void queryClient.invalidateQueries({ queryKey: passkeyQueryKey });
    },
  });
  const pending = addPasskey.isPending || removePasskey.isPending;
  const error = addPasskey.error ?? removePasskey.error;

  return (
    <section className="settings-card">
      <h2>{t("passkeys.title")}</h2>
      <p>{t("passkeys.description")}</p>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="alert" role="alert">
          {error.message}
        </p>
      )}
      <div className="settings-row passkey-enrolment">
        <label>
          {t("passkeys.name")}
          <input
            value={name}
            maxLength={64}
            onChange={(event) => setName(event.currentTarget.value)}
          />
        </label>
        <button
          className="primary"
          disabled={pending || name.trim().length === 0}
          onClick={() => {
            setNotice(undefined);
            addPasskey.mutate(name.trim());
          }}
        >
          {addPasskey.isPending ? t("passkeys.adding") : t("passkeys.add")}
        </button>
      </div>
      {Match.value(passkeys).pipe(
        Match.when({ status: "pending" }, () => (
          <p className="muted" role="status">
            {t("passkeys.loading")}
          </p>
        )),
        Match.when({ status: "error" }, ({ error: queryError }) =>
          Match.valueTags(queryError as ApiError, {
            ApiError: (failure) => (
              <p className="alert" role="alert">
                {failure.message}
              </p>
            ),
          }),
        ),
        Match.when({ status: "success" }, ({ data }) =>
          data.length === 0 ? (
            <p className="muted">{t("passkeys.none")}</p>
          ) : (
            <ul className="passkey-list">
              {data.map((passkey) => (
                <li key={passkey.id}>
                  <span>
                    <strong>{passkey.name}</strong>
                    {passkey.createdAt && (
                      <small>
                        {t("passkeys.addedOn")} {new Date(passkey.createdAt).toLocaleDateString(language)}
                      </small>
                    )}
                  </span>
                  <button
                    className="secondary danger"
                    disabled={pending}
                    onClick={() => {
                      setNotice(undefined);
                      removePasskey.mutate(passkey.id);
                    }}
                  >
                    {t("passkeys.remove")}
                  </button>
                </li>
              ))}
            </ul>
          ),
        ),
        Match.exhaustive,
      )}
    </section>
  );
}
